import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';

import ModalDialog from '../../../ModalDialog';
import styles from './extraInfoModal.module.css';
import TextInput from '../../../TextInput';
import TabsContainer from '../../../TabsContainer';
import TabPane from '../../../TabPane';
import RichTextEditor from '../../../RichTextEditor';
import { extraInfoConstants, AVAILABLE_LANGUAGES } from '../../../../constants';

const { TITLE_MAX_LENGTH, INFO_MAX_LENGTH } = extraInfoConstants;

const validateExtraInfo = (titleValues, infoValues) => {
  const perLanguageValidity = AVAILABLE_LANGUAGES.map((lang) => {
    const hasTitle = titleValues[lang] && titleValues[lang].length <= TITLE_MAX_LENGTH;
    const hasInfo = infoValues[lang] && infoValues[lang].length <= INFO_MAX_LENGTH;

    return { hasTitle, hasInfo };
  });

  const allHaveBothValuesOrNeither = perLanguageValidity.every(({ hasTitle, hasInfo }) => hasTitle === hasInfo);
  const hasAtLeastOneComplete = perLanguageValidity.some(({ hasTitle, hasInfo }) => hasTitle && hasInfo);

  return hasAtLeastOneComplete && allHaveBothValuesOrNeither;
};

const ExtraInfoModal = ({
  isOpen,
  onClose,
  onSubmit,
  dimensionName,
  title,
  info,
  translate
}) => {
  const modalContentRef = useRef(null);
  const [titleValue, setTitleValue] = useState(title);
  const [infoValue, setInfoValue] = useState(info);

  const isValidForm = useMemo(
    () => validateExtraInfo(titleValue, infoValue),
    [titleValue, infoValue]
  );

  const handleConfirm = () => {
    onSubmit(titleValue, infoValue);
  };

  const scrollToBottom = useCallback(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = modalContentRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // The input depends on which tab was open when the modal was closed
      document.querySelector('#extraInfoModalContent input').focus();
    }
  }, [isOpen]);

  const tabContent = useCallback((lang) => (
    <>
      <div className={styles.textAreaContainer}>
        <label htmlFor={`title${lang}`}>
          {translate(`surveySchemaParts.dimensions.extraInfo.title${lang}`)}
          <span className={styles.requiredText}> ({translate('common.required')})</span>
        </label>
        <TextInput
          value={titleValue[lang]}
          onChange={(e) => setTitleValue({ ...titleValue, [lang]: e.target.value })}
          maxLength={TITLE_MAX_LENGTH}
          id={`title${lang}`}
        />
      </div>
      <div className={styles.textAreaContainer}>
        <label>
          {translate(`surveySchemaParts.dimensions.extraInfo.info${lang}`)}
          <span className={styles.requiredText}> ({translate('common.required')})</span>
        </label>
        <RichTextEditor
          onChange={(e) => setInfoValue({ ...infoValue, [lang]: e })}
          defaultValue={infoValue[lang]}
          hideButtonsOnBlur
          maxLength={INFO_MAX_LENGTH}
          onHeightChange={scrollToBottom}
          isLarge
        />
      </div>
    </>
  ), [titleValue, setTitleValue, infoValue, setInfoValue]);

  return (
    <ModalDialog
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onCancel={onClose}
      isValid={isValidForm}
      confirmTranslationKey="common.save"
      cancelTranslationKey="common.close"
      title="surveySchemaParts.dimensions.extraInfo.edit"
      variantButtons
      isLarge
    >
      <div className={styles.modalContent} id="extraInfoModalContent" ref={modalContentRef}>
        {/* eslint-disable-next-line react/no-danger */}
        <p dangerouslySetInnerHTML={{ __html: translate('surveySchemaParts.dimensions.extraInfo.editDescription', { dimensionName }) }} />
        <span className={styles.requiredText}> * {translate('surveySchemaParts.dimensions.extraInfo.requiredDescription')} </span>
        <TabsContainer skinny>
          {AVAILABLE_LANGUAGES.map((lang) => (
            <TabPane label={translate(`inLanguage.${lang}`).toUpperCase()} key={`tab-${lang}`}>
              {tabContent(lang)}
            </TabPane>
          ))}
        </TabsContainer>
      </div>
    </ModalDialog>
  );
};

const {
  bool, func, string, shape
} = PropTypes;

ExtraInfoModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onSubmit: func.isRequired,
  dimensionName: string.isRequired,
  title: shape(),
  info: shape(),
  translate: func.isRequired
};

ExtraInfoModal.defaultProps = {
  title: { fi: '', sv: '', en: '' },
  info: { fi: '', sv: '', en: '' }
};

export default ExtraInfoModal;
